import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";

const formatUtcToBrowserDate = (time) => {
  if (!time) return null;

  const normalizedTime =
    typeof time === "string" && !time.endsWith("Z") ? `${time}Z` : time;

  return dayjs(normalizedTime);
};

const WirelessCongestionLevelChart = ({ data }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const chartData = useMemo(() => {
    const series = Array.isArray(data) ? data : (data?.series ?? []);
    const dataMap = new Map();

    series.forEach((item) => {
      if (!Array.isArray(item)) return;

      const [time, value] = item;
      const browserTime = formatUtcToBrowserDate(time);

      if (!browserTime?.isValid()) return;

      const key = browserTime.format("YYYY-MM-DD HH");

      dataMap.set(key, Number(value) || 0);
    });

    const lastTime =
      series.length > 0
        ? formatUtcToBrowserDate(series[series.length - 1]?.[0])
        : dayjs().startOf("hour");

    const endTime =
      lastTime?.isValid() === true
        ? lastTime.startOf("hour")
        : dayjs().startOf("hour");

    const startTime = endTime.subtract(23, "hour");

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const key = currentTime.format("YYYY-MM-DD HH");

      return {
        label: currentTime.format("HH:mm"),
        fullTime: currentTime.format("YYYY/MM/DD HH:mm"),
        value: dataMap.get(key) ?? 0,
      };
    });
  }, [data]);

  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return;

    const chart = chartInstanceRef.current || echarts.init(chartRef.current);

    chartInstanceRef.current = chart;

    const xAxisData = chartData.map((item) => item.label);
    const yAxisData = chartData.map((item) => item.value);

    const maxPoint = chartData.reduce(
      (prev, current) => (current.value > prev.value ? current : prev),
      chartData[0],
    );

    const option = {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      grid: {
        top: 14,
        right: 18,
        bottom: 32,
        left: 42,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter(params) {
          const point = params?.[0];

          if (!point) return "";

          const currentData = chartData[point.dataIndex] || {};

          return `${currentData.fullTime || point.axisValue}<br/>Aps: ${
            point.data
          }`;
        },
        axisPointer: {
          type: "line",
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dashed",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xAxisData,
        axisLine: {
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
          interval: 0,
          formatter: (value, index) => {
            const isLast = index === xAxisData.length - 1;
            return index % 4 === 0 || isLast ? value : "";
          },
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dotted",
          },
        },
      },

      yAxis: {
        type: "value",
        min: 0,
        max: 90,
        interval: 30,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dotted",
          },
        },
      },

      series: [
        {
          type: "line",
          data: yAxisData,
          smooth: false,
          showSymbol: false,
          symbol: "none",
          lineStyle: {
            color: theme.palette.primary.orangeThird,
            width: 2,
          },

          markPoint: {
            symbol: "circle",
            symbolSize: 0,
            data: [
              {
                coord: [maxPoint.label, maxPoint.value],
                value: maxPoint.value,
              },
            ],
            label: {
              show: true,
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[11],
              fontWeight: theme.typography.customFontWeight[1],
              offset: [0, -10],
              formatter: ({ value }) => value,
            },
          },
        },
      ],
    };

    chart.setOption(option, true);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, theme]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: "320px",
      }}
    />
  );
};

export default WirelessCongestionLevelChart;